import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  FolderOpen, CheckCircle, XCircle, Clock, RefreshCw, 
  Search, FileText, User, Eye, ChevronDown, ChevronUp
} from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

export default function OnboardingStatusDashboard() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get('/operations/onboarding-status');
      setData(res.data);
    } catch (error) {
      console.error('Onboarding status fetch error:', error);
      toast.error('Failed to load onboarding status');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = data?.employees?.filter(emp => 
    emp.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getStatusBadge = (employee) => {
    if (employee.completed) {
      return <Badge className="bg-emerald-100 text-emerald-700">Complete</Badge>;
    } else if (employee.progress_percent >= 75) {
      return <Badge className="bg-amber-100 text-amber-700">Almost Done</Badge>;
    } else if (employee.progress_percent >= 50) {
      return <Badge className="bg-blue-100 text-blue-700">In Progress</Badge>;
    } else {
      return <Badge className="bg-red-100 text-red-700">Needs Attention</Badge>;
    }
  };

  const docTypeLabels = {
    contract: 'Employment Contract',
    sin: 'SIN Document',
    banking: 'Banking Information',
    tax: 'Tax Forms (TD1)'
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="onboarding-status-dashboard">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Onboarding Status</h1>
          <p className="text-slate-500">Track employee onboarding document completion</p>
        </div>
        <Button onClick={fetchOnboardingStatus} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total Employees</p>
                <p className="text-2xl font-bold text-slate-900">{data?.total_employees || 0}</p>
              </div>
              <User className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Completed</p>
                <p className="text-2xl font-bold text-emerald-600">{data?.completed_count || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Incomplete</p>
                <p className="text-2xl font-bold text-amber-600">{data?.incomplete_count || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Completion Rate</p>
                <p className="text-2xl font-bold text-blue-600">
                  {data?.total_employees ? Math.round((data.completed_count / data.total_employees) * 100) : 0}%
                </p>
              </div>
              <FolderOpen className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Employee Onboarding Details</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-employees"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredEmployees.length === 0 ? (
            <div className="text-center py-8">
              <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No employees found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredEmployees.map((employee) => (
                <div 
                  key={employee.employee_id} 
                  className="border rounded-lg overflow-hidden"
                  data-testid={`employee-row-${employee.employee_id}`}
                >
                  {/* Employee Header */}
                  <div 
                    className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      employee.completed ? 'bg-emerald-50/50' : ''
                    }`}
                    onClick={() => setExpandedEmployee(
                      expandedEmployee === employee.employee_id ? null : employee.employee_id
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                          <User className="h-5 w-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{employee.employee_name}</p>
                          <p className="text-sm text-slate-500">{employee.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            {getStatusBadge(employee)}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Hired: {employee.hired_date ? format(new Date(employee.hired_date), 'MMM d, yyyy') : 'N/A'}
                          </p>
                        </div>
                        <div className="w-24">
                          <Progress value={employee.progress_percent} className="h-2" />
                          <p className="text-xs text-center mt-1">{employee.progress_percent}%</p>
                        </div>
                        {expandedEmployee === employee.employee_id ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedEmployee === employee.employee_id && (
                    <div className="border-t p-4 bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Required Documents */}
                        <div>
                          <h4 className="text-sm font-medium text-slate-700 mb-3">Required Documents</h4>
                          <div className="space-y-2">
                            {employee.required_documents?.map((docType) => {
                              const uploaded = employee.uploaded_documents?.find(d => d.doc_type === docType);
                              return (
                                <div 
                                  key={docType}
                                  className={`flex items-center justify-between p-2 rounded ${
                                    uploaded ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <FileText className={`h-4 w-4 ${uploaded ? 'text-emerald-500' : 'text-red-500'}`} />
                                    <span className="text-sm">{docTypeLabels[docType] || docType}</span>
                                  </div>
                                  {uploaded ? (
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-slate-500">
                                        {format(new Date(uploaded.uploaded_at), 'MMM d')}
                                      </span>
                                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                                    </div>
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Missing Documents */}
                        {employee.missing_documents?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-700 mb-3">Missing Documents</h4>
                            <div className="space-y-2">
                              {employee.missing_documents.map((docType) => (
                                <div 
                                  key={docType}
                                  className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded"
                                >
                                  <XCircle className="h-4 w-4 text-amber-500" />
                                  <span className="text-sm text-amber-700">{docTypeLabels[docType] || docType}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-4 flex gap-2">
                        <Link to={`/admin/onboarding?employee=${employee.employee_id}`}>
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4 mr-2" />
                            View Documents
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
